import React, { useContext, useEffect, useState, useCallback } from "react";
import { AuthContext } from "../state/AuthContext";
import SpeechInput from "./SpeechInput";

export default function PaymentReceiptModal({ enquiry, onClose, onPaymentRecorded }) {
  const { http } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [summary, setSummary] = useState({
    totalWorkFee: Number(enquiry?.latestPaymentAmount || 0),
    totalPaid: Number(enquiry?.totalPaid || 0),
    pendingBalance: Number(enquiry?.pendingBalance || 0),
  });

  const [activeTab, setActiveTab] = useState("collect"); // "collect" | "receipts" | "print"
  const [selectedReceipt, setSelectedReceipt] = useState(null);

  // Form state
  const [amountPaid, setAmountPaid] = useState("");
  const [paymentMode, setPaymentMode] = useState("CASH");
  const [referenceNo, setReferenceNo] = useState("");
  const [paymentNotes, setPaymentNotes] = useState("");

  const loadReceipts = useCallback(async () => {
    if (!enquiry?.id) return;
    setLoading(true);
    setError(null);
    try {
      const res = await http.get(`/api/enquiries/${enquiry.id}/payments`);
      setReceipts(res.data.receipts || []);
      if (res.data.summary) {
        setSummary(res.data.summary);
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load payment history.");
    } finally {
      setLoading(false);
    }
  }, [enquiry?.id, http]);

  useEffect(() => {
    loadReceipts();
  }, [loadReceipts]);

  const handleRecordPayment = async (e) => {
    e.preventDefault();
    const numericAmount = Number(amountPaid);
    if (!numericAmount || numericAmount <= 0) {
      setError("Please enter a valid amount greater than 0.");
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const res = await http.post(`/api/enquiries/${enquiry.id}/payments`, {
        amountPaid: numericAmount,
        paymentMode,
        referenceNo,
        paymentNotes,
      });

      setAmountPaid("");
      setReferenceNo("");
      setPaymentNotes("");
      setSelectedReceipt(res.data.receipt);
      setActiveTab("print");
      await loadReceipts();
      if (onPaymentRecorded) onPaymentRecorded();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to record payment receipt.");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const remainingAfterPayment = Math.max(
    0,
    summary.pendingBalance - (Number(amountPaid) || 0)
  );

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content shadow">
          <div className="modal-header bg-dark text-white print-hide">
            <h5 className="modal-title">
              💳 Payments & Receipts - {enquiry?.applicationNo} ({enquiry?.customerName})
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>

          <div className="modal-body">
            {/* Tabs (Hidden during print) */}
            <ul className="nav nav-tabs mb-3 print-hide">
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "collect" ? "active fw-bold" : ""}`}
                  onClick={() => setActiveTab("collect")}
                >
                  💳 Collect Payment
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "receipts" ? "active fw-bold" : ""}`}
                  onClick={() => setActiveTab("receipts")}
                >
                  📄 Receipt History ({receipts.length})
                </button>
              </li>
              {selectedReceipt ? (
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === "print" ? "active fw-bold" : ""}`}
                    onClick={() => setActiveTab("print")}
                  >
                    🖨️ Printable Receipt ({selectedReceipt.receiptNo})
                  </button>
                </li>
              ) : null}
            </ul>

            {error ? <div className="alert alert-danger print-hide">{error}</div> : null}

            {/* TAB 1: Collect Payment */}
            {activeTab === "collect" ? (
              <div className="print-hide">
                <div className="row g-3 mb-4">
                  <div className="col-12 col-md-4">
                    <div className="card bg-light border-0">
                      <div className="card-body py-2">
                        <div className="small text-muted">Total Work Fee</div>
                        <div className="fs-5 fw-bold text-dark">
                          ₹{Number(summary.totalWorkFee).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="card bg-light border-0">
                      <div className="card-body py-2">
                        <div className="small text-muted">Total Paid</div>
                        <div className="fs-5 fw-bold text-success">
                          ₹{Number(summary.totalPaid).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 col-md-4">
                    <div className="card bg-light border-0">
                      <div className="card-body py-2">
                        <div className="small text-muted">Pending Balance</div>
                        <div className="fs-5 fw-bold text-danger">
                          ₹{Number(summary.pendingBalance).toFixed(2)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleRecordPayment} className="row g-3">
                  <div className="col-12 col-md-6">
                    <label className="form-label fw-bold">Amount Paid (₹) *</label>
                    <input
                      type="number"
                      step="0.01"
                      min="1"
                      className="form-control form-control-lg"
                      value={amountPaid}
                      onChange={(e) => setAmountPaid(e.target.value)}
                      placeholder="e.g. 500"
                      required
                    />
                    {amountPaid ? (
                      <div className="form-text text-primary">
                        New Balance will be: ₹{remainingAfterPayment.toFixed(2)}
                      </div>
                    ) : null}
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label fw-bold">Payment Mode *</label>
                    <select
                      className="form-select form-select-lg"
                      value={paymentMode}
                      onChange={(e) => setPaymentMode(e.target.value)}
                    >
                      <option value="CASH">💵 CASH</option>
                      <option value="UPI">📱 UPI / QR</option>
                      <option value="ONLINE">💳 ONLINE / CARD</option>
                      <option value="BANK_TRANSFER">🏦 BANK TRANSFER</option>
                      <option value="CHEQUE">📝 CHEQUE</option>
                    </select>
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label">Reference / UTR / Cheque No</label>
                    <input
                      type="text"
                      className="form-control"
                      value={referenceNo}
                      onChange={(e) => setReferenceNo(e.target.value)}
                      placeholder="e.g. UTR1298471928"
                    />
                  </div>

                  <div className="col-12 col-md-6">
                    <label className="form-label">Payment Notes / Remark</label>
                    <SpeechInput
                      as="input"
                      name="paymentNotes"
                      value={paymentNotes}
                      onChange={(e) => setPaymentNotes(e.target.value)}
                      placeholder="e.g. First installment paid"
                    />
                  </div>

                  <div className="col-12 text-end mt-4">
                    <button
                      type="button"
                      className="btn btn-outline-secondary me-2"
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-success btn-lg"
                      disabled={submitting}
                    >
                      {submitting ? "Saving Receipt..." : "💾 Save & Generate Receipt"}
                    </button>
                  </div>
                </form>
              </div>
            ) : null}

            {/* TAB 2: Receipt History */}
            {activeTab === "receipts" ? (
              <div className="print-hide">
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Receipt No</th>
                        <th>Date & Time</th>
                        <th>Amount Paid</th>
                        <th>Mode</th>
                        <th>Ref No</th>
                        <th>Collected By</th>
                        <th className="text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan={7} className="text-center py-3 text-muted">
                            Loading payment history...
                          </td>
                        </tr>
                      ) : null}

                      {!loading && receipts.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-center py-3 text-muted">
                            No payment receipts generated yet.
                          </td>
                        </tr>
                      ) : null}

                      {!loading &&
                        receipts.map((r) => (
                          <tr key={r.id}>
                            <td className="fw-bold">{r.receiptNo}</td>
                            <td className="small">
                              {new Date(r.createdAt).toLocaleString()}
                            </td>
                            <td className="fw-bold text-success">
                              ₹{Number(r.amountPaid).toFixed(2)}
                            </td>
                            <td>
                              <span className="badge text-bg-light border">{r.paymentMode}</span>
                            </td>
                            <td className="small text-muted">{r.referenceNo || "-"}</td>
                            <td>{r.createdByName}</td>
                            <td className="text-end">
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => {
                                  setSelectedReceipt(r);
                                  setActiveTab("print");
                                }}
                              >
                                🖨️ View Receipt
                              </button>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}

            {/* TAB 3: Printable Receipt */}
            {activeTab === "print" && selectedReceipt ? (
              <div id="printable-receipt-content" className="p-3">
                <div className="border border-2 border-dark p-4 rounded bg-white">
                  <div className="d-flex justify-content-between align-items-start border-bottom pb-3 mb-3">
                    <div>
                      <h3 className="fw-bold mb-1 text-primary">MK MOTORS SERVICES</h3>
                      <div className="small text-muted">Official Payment Receipt</div>
                    </div>
                    <div className="text-end">
                      <div className="fw-bold text-danger fs-5">{selectedReceipt.receiptNo}</div>
                      <div className="small text-muted">
                        Date: {new Date(selectedReceipt.createdAt).toLocaleString()}
                      </div>
                    </div>
                  </div>

                  <div className="row g-3 mb-4">
                    <div className="col-6">
                      <div className="small text-muted">Customer Name:</div>
                      <div className="fw-bold text-uppercase">{enquiry?.customerName}</div>
                      <div className="small">{enquiry?.customerMobile}</div>
                    </div>
                    <div className="col-6 text-end">
                      <div className="small text-muted">Application No:</div>
                      <div className="fw-bold">{enquiry?.applicationNo}</div>
                      <div className="small text-muted">
                        Work: {enquiry?.latestWorkTypeName || enquiry?.workTypeName || "-"} -{" "}
                        {enquiry?.latestWorkDescription || enquiry?.workDescription || "-"}
                      </div>
                    </div>
                  </div>

                  <table className="table table-bordered align-middle mb-4">
                    <thead className="table-light">
                      <tr>
                        <th>Particulars / Payment Description</th>
                        <th>Payment Mode</th>
                        <th>Reference / UTR No</th>
                        <th className="text-end">Amount Paid (₹)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          Payment received for RTO Work ({enquiry?.latestWorkTypeName || "Services"})
                        </td>
                        <td>
                          <span className="fw-semibold">{selectedReceipt.paymentMode}</span>
                        </td>
                        <td>{selectedReceipt.referenceNo || "-"}</td>
                        <td className="text-end fw-bold fs-5 text-success">
                          ₹{Number(selectedReceipt.amountPaid).toFixed(2)}
                        </td>
                      </tr>
                    </tbody>
                  </table>

                  <div className="card bg-light border-0 mb-4">
                    <div className="card-body">
                      <h6 className="fw-bold mb-2">Account Summary</h6>
                      <div className="row g-2 small">
                        <div className="col-4">
                          <span className="text-muted">Total Work Fee:</span>{" "}
                          <span className="fw-bold">₹{Number(summary.totalWorkFee).toFixed(2)}</span>
                        </div>
                        <div className="col-4">
                          <span className="text-muted">Total Paid Till Date:</span>{" "}
                          <span className="fw-bold text-success">
                            ₹{Number(summary.totalPaid).toFixed(2)}
                          </span>
                        </div>
                        <div className="col-4 text-end">
                          <span className="text-muted">Remaining Balance:</span>{" "}
                          <span className="fw-bold text-danger">
                            ₹{Number(summary.pendingBalance).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="d-flex justify-content-between align-items-end mt-4 pt-3 border-top">
                    <div className="small text-muted">
                      Issued By: <strong>{selectedReceipt.createdByName || "Authorized User"}</strong>
                    </div>
                    <div className="text-end">
                      <div className="fw-bold border-top border-dark pt-1 px-4 d-inline-block">
                        Authorized Signatory
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-center mt-3 print-hide">
                  <button className="btn btn-primary me-2" onClick={handlePrint}>
                    🖨️ Print Receipt
                  </button>
                  <button
                    className="btn btn-outline-secondary"
                    onClick={() => setActiveTab("collect")}
                  >
                    Back to Collect
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
